import type { Reducer } from '@reduxjs/toolkit';
import { createAction, createSelector } from '@reduxjs/toolkit';
import type { DeepReadonly } from 'ts-essentials';

import { getOrderingSchedule } from '@jane/shared-ecomm/util';
import type { OrderingSchedule } from '@jane/shared-ecomm/util';
/* eslint-disable-next-line no-restricted-imports */
import type { Id, NoStore, Store } from '@jane/shared/models';
import { isStore } from '@jane/shared/util';

import { StoreSource } from '../../sources/store';
import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppState } from '../redux';
import * as EmbeddedAppActions from './embeddedApp';

export const GET_STORE = 'store/get';
export const initiateGetStore = createAction<void, typeof GET_STORE>(GET_STORE);

export const GET_STORE_SUCCESS = 'store/get-success';
export const getStoreSuccess = createAction<
  { store: Store },
  typeof GET_STORE_SUCCESS
>(GET_STORE_SUCCESS);

export const GET_STORE_ERROR = 'store/get-error';
export const getStoreError = createAction<unknown, typeof GET_STORE_ERROR>(
  GET_STORE_ERROR
);

interface GetStoreParams {
  id: Id;
  params?: unknown;
}
export const getStore =
  ({ id, params }: GetStoreParams): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateGetStore());
    StoreSource.get(id, params).then(
      (result) => dispatch(getStoreSuccess(result)),
      (err) => dispatch(getStoreError(err))
    );
  };

export type StoreActions =
  | ReturnType<typeof initiateGetStore>
  | ReturnType<typeof getStoreSuccess>
  | ReturnType<typeof getStoreError>;

export type StoreState = DeepReadonly<{
  hasLoaded: boolean;
  isLoadingStore: boolean;
  store: Store | NoStore;
}>;

const getInitialState = (): StoreState => ({
  hasLoaded: false,
  isLoadingStore: false,
  store: {
    noStore: true,
  },
});

export const storeReducer: Reducer<StoreState, CustomerAction> = (
  state = getInitialState(),
  action
) => {
  switch (action.type) {
    case GET_STORE:
    case EmbeddedAppActions.VERIFY_STORE:
      return { ...state, isLoadingStore: true };

    case EmbeddedAppActions.VERIFY_STORE_SUCCESS:
    case GET_STORE_SUCCESS: {
      const { store } = action.payload;
      const newState = {
        ...state,
        store,
        isLoadingStore: false,
        hasLoaded: true,
      };
      return newState;
    }

    case GET_STORE_ERROR:
      return { ...state, isLoadingStore: false };
    default:
      break;
  }

  return state;
};

interface StoreStateWithSchedules extends StoreState {
  schedules: OrderingSchedule | undefined;
}

export const selectStore = createSelector(
  [(state: AppState) => state.store],
  (dispensaryState): StoreStateWithSchedules => {
    const dispensary = dispensaryState.store;

    return {
      ...dispensaryState,
      schedules: isStore(dispensary)
        ? getOrderingSchedule({
            store: dispensary,
            type: ['curbside', 'delivery', 'pickup', 'retail'],
          })
        : undefined,
    };
  }
);
